/**
 *  Created by piotr@revisual.io on 13/08/2025
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSubscriptionPlansStore } from "../../../../hooks/redux/subscriptionPlans";
import SubscriptionPlanColumn from "./SubscriptionPlanColumn";
import classNames from "classnames";
import SubscriptionIntervalButton from "./SubscriptionIntervalButton";
import { useCurrentOrganizationStore } from "../../../../hooks/redux/currentOrganization";
import { hasSubscription } from "../../helpers/organization";
import { PLANS } from "../../../../consts";
import { isNewPlanIsBelowUsage } from "../../helpers/subscription";
import PlanComparisonTable from "./PlanComparisonTable";
import PlanDowngradeModal from "./PlanDowngradeModal";
import { getReferralCoupon } from "../../../../actions/plans";
import { useSubscriptionStore } from "../../../../hooks/redux/subscription";
import ApiError from "../ApiError";

const euroCurrencyRegions = [
  // Eurozone EU members
  "AT", // Austria
  "BE", // Belgium
  "CY", // Cyprus
  "EE", // Estonia
  "FI", // Finland
  "FR", // France
  "DE", // Germany
  "GR", // Greece
  "IE", // Ireland
  "IT", // Italy
  "LV", // Latvia
  "LT", // Lithuania
  "LU", // Luxembourg
  "MT", // Malta
  "NL", // Netherlands
  "PT", // Portugal
  "SK", // Slovakia
  "SI", // Slovenia
  "ES", // Spain

  // Non-EU but in Europe using EUR
  "AD", // Andorra
  "MC", // Monaco
  "SM", // San Marino
  "VA", // Vatican City
  "ME", // Montenegro
  "XK", // Kosovo

  // EU members not using EUR
  "BG", // Bulgaria - BGN
  "HR", // Croatia - HRK
  "CZ", // Czechia - CZK
  "DK", // Denmark - DKK
  "HU", // Hungary - HUF
  "PL", // Poland - PLN
  "RO", // Romania - RON
  "SE", // Sweden - SEK

  // Non-EU European countries
  "AL", // Albania - ALL
  "AM", // Armenia - AMD
  "AZ", // Azerbaijan - AZN
  "BA", // Bosnia and Herzegovina - BAM
  "BY", // Belarus - BYN
  "CH", // Switzerland - CHF
  "GE", // Georgia - GEL
  "IS", // Iceland - ISK
  "LI", // Liechtenstein - CHF
  "MD", // Moldova - MDL
  "MK", // North Macedonia - MKD
  "NO", // Norway - NOK
  "RS", // Serbia - RSD
  "TR", // Türkiye - TRY
  "UA", // Ukraine - UAH
  "GB", // United Kingdom - GBP
];

// get language from navigator and set defaultCurrency const based on region. if region is in euroCurrencyRegions then set defaultCurrency to "eur" otherwise set it to "usd"
const userLanguage = navigator.language || navigator.userLanguage || "en-US";
const defaultCurrency = euroCurrencyRegions.includes(
  userLanguage.slice(-2).toUpperCase()
)
  ? "eur"
  : "usd";

export const SubscriptionsPricingTable = () => {
  const { currentOrganization } = useCurrentOrganizationStore();
  const subscription = hasSubscription(currentOrganization);
  const { subscriptionPlans } = useSubscriptionPlansStore();
  const {
    createStripeCheckoutSession,
    subscription: subscriptionStore,
    fetchCustomerPortalLink,
  } = useSubscriptionStore();

  const [interval, setInterval] = useState("month");
  const [currency, setCurrency] = useState(defaultCurrency);
  const [showPlanComparisonModal, setShowPlanComparisonModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [showCurrencySelector, setShowCurrencySelector] = useState(false);

  const hadTrial = useMemo(
    () =>
      currentOrganization.model.organizations_plans.find((i) =>
        Number.parseInt(i.is_trial)
      ) !== undefined,
    [currentOrganization]
  );

  const isLoading =
    subscriptionStore.fetch ||
    subscriptionStore.startCheckoutSession ||
    subscriptionStore.stripeBillingPortalLinkFetch ||
    subscriptionStore.stripeCustomerSessionFetch ||
    subscriptionStore.stripePaymentLinkFetch;

  useEffect(() => {
    if (subscription) {
      // todo it should decide what currency to show, stripe does not allow changing currency
    }
  }, [subscription]);

  useEffect(() => {
    if (subscriptionPlans.fetchSuccess) {
      const currencies = Object.keys(subscriptionPlans.collection[0].prices);

      if (!currencies.includes(currency)) {
        setCurrency(currencies[0]); // Fallback to first available currency
      }
    }
  }, [subscriptionPlans.fetchSuccess]);

  const stripeProducts = useMemo(() => {
    return subscriptionPlans.collection
      .sort((a, b) => {
        // sort by price ascending
        const aPrice = a.prices[currency]?.find(
          (price) => price.recurring?.interval.localeCompare(interval) === 0
        )?.unit_amount;
        const bPrice = b.prices[currency]?.find(
          (price) => price.recurring?.interval.localeCompare(interval) === 0
        )?.unit_amount;
        return (aPrice || 0) - (bPrice || 0);
      })
      .reduce((acc, product) => {
        if (product.prices[currency]?.length > 0) {
          acc[product.id] = product;
        }
        return acc;
      }, {});
  }, [subscriptionPlans, currency, interval]);

  const stripePrices = useMemo(() => {
    return subscriptionPlans.collection.reduce((acc, product) => {
      const price = product.prices[currency]?.find(
        (price) => price.recurring?.interval.localeCompare(interval) === 0
      );

      if (price && price.active) {
        acc[product.id] = price;
      }
      return acc;
    }, {});
  }, [subscriptionPlans, interval, currency]);

  const onIntervalChange = useCallback(
    (newInterval) => () => {
      setInterval(newInterval);
    },
    [setInterval]
  );

  const onChangeCurrency = useCallback(
    (event) => {
      setCurrency(event.target.value);
    },
    [setCurrency]
  );

  const onSelectPrice = useCallback(
    (price, plan) => () => {
      // check if new plan would meet the current usage limits
      // if not, show a modal with summary of current usage and new plan limits
      // else, proceed with subscription
      const oldPlanIndex = Object.values(PLANS).indexOf(subscription.plan_code);
      const newPlanIndex = Object.values(PLANS).indexOf(
        plan.metadata["app_plan_code"]
      );

      if (
        !subscription.is_trial &&
        subscription.plan_code !== PLANS.free &&
        oldPlanIndex >= newPlanIndex &&
        isNewPlanIsBelowUsage(plan, currentOrganization)
      ) {
        setSelectedPlan(plan);
        setSelectedPrice(price);
        setShowPlanComparisonModal(true);
      } else {
        onSubmit(price, plan);
      }
    },
    [showPlanComparisonModal, subscription]
  );

  const onSubmit = useCallback((price, plan) => {
    // check if it is a new subscription or upgrade/downgrade
    if (subscription.plan_code !== PLANS.free) {
      fetchCustomerPortalLink("subscription_update");
    } else {
      createStripeCheckoutSession(price.id);
    }
  }, []);

  const onCloseComparisonModal = useCallback(() => {
    setShowPlanComparisonModal(false);
    setSelectedPlan(null);
    setSelectedPrice(null);
  }, [setShowPlanComparisonModal, setSelectedPlan]);

  const columns = Object.values(stripePrices).map((price, idx) => {
    const stripeProduct = stripeProducts[price.product];
    let actionLabel = "Subscribe";
    let isCurrentPlan = false;

    if (!hadTrial) {
      actionLabel = "Start Free Trial";
    } else if (!subscription.is_trial) {
      const pricePlanCode = stripeProduct.metadata["app_plan_code"];
      // determine label either Upgrade or Downgrade based on PLANS object keys. if key has higher index then it is Upgrade otherwise Downgrade
      const planKeys = Object.values(PLANS);
      const currentPlanIndex = planKeys.indexOf(subscription.plan_code);
      const pricePlanIndex = planKeys.indexOf(pricePlanCode);

      if (!subscription.is_trial) {
        if (currentPlanIndex < pricePlanIndex) {
          actionLabel = "Upgrade";
        } else if (currentPlanIndex > pricePlanIndex) {
          actionLabel = "Downgrade";
        } else {
          const priceInterval = price.recurring.interval; // "month" or "year"
          const isMonthly = priceInterval === "month";

          if (price.id === subscription.stripe_price_id) {
            actionLabel = "Current Plan";
            isCurrentPlan = true;
          } else if (subscription.is_annual && isMonthly) {
            actionLabel = "Downgrade";
          } else if (!subscription.is_annual && !isMonthly) {
            actionLabel = "Upgrade";
          }
        }
      }
    }

    return (
      <SubscriptionPlanColumn
        key={price.id}
        plan={stripeProduct}
        price={price}
        onAction={onSelectPrice}
        showAction={true}
        showImage={false}
        showBestDealLabel={idx === 1}
        index={idx}
        currency={currency}
        actionLabel={actionLabel}
        discount={subscriptionPlans.coupon}
        disabled={isCurrentPlan || isLoading}
      />
    );
  });

  const currencyOptions = Object.keys(
    subscriptionPlans.collection[0].prices
  ).map((currency) => (
    <option key={currency} value={currency}>
      {currency.toUpperCase()}
    </option>
  ));

  const currencySelector =
    currencyOptions.length > 1 ? (
      <div className={"currency-select-wrapper"}>
        <select className={"currency-select"} onChange={onChangeCurrency}>
          {currencyOptions}
        </select>
      </div>
    ) : null;

  // console.log(subscriptionStore.stripeBillingPortalLinkFetchError);
  return (
    <div>
      <ApiError
        error={subscriptionStore.stripeCustomerSessionFetchError}
        heading={"Couldn't start Customer Session"}
      />
      <ApiError
        error={subscriptionStore.startCheckoutSessionError}
        heading={"Couldn't start Checkout Session"}
      />
      <ApiError
        error={subscriptionStore.stripeBillingPortalLinkFetchError}
        heading={"Couldn't start Billing Portal Session"}
      />
      {showPlanComparisonModal ? (
        <PlanDowngradeModal
          selectedPlan={selectedPlan}
          selectedPrice={selectedPrice}
          onCancel={onCloseComparisonModal}
          onSubmit={onSubmit}
        />
      ) : null}
      <div className={"subscription-plan-table-selectors"}>
        <div></div>
        <div className="subscription-plan-table-selectors-interval-container">
          <div
            role="tablist"
            aria-orientation="horizontal"
            aria-label="Billing intervals"
            className="subscription-plan-table-selectors-interval"
          >
            <SubscriptionIntervalButton
              onIntervalChange={onIntervalChange}
              isSelected={interval === "month"}
              label="Monthly"
              interval="month"
            />
            <SubscriptionIntervalButton
              onIntervalChange={onIntervalChange}
              isSelected={interval === "year"}
              label="Yearly"
              interval="year"
            />

            <span
              className={classNames(
                "subscription-plan-table-selectors-interval-button_glider",
                {
                  "subscription-plan-table-selectors-interval-button_glider-right":
                    interval !== "month",
                }
              )}
            ></span>
          </div>
        </div>
        <div>{currencySelector}</div>
      </div>
      <div className={"subscription-plan-table"}>{columns}</div>
    </div>
  );
};

export default SubscriptionsPricingTable;
